import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Shield, ShieldOff, UserX, Search, Clock } from 'lucide-react'
import { toast } from 'sonner'
import { formatDate } from '@/utils/format-date'
import api from '@/services/api'

interface BlockedUser {
  id: string
  name: string
  email: string
  reason: string
  blockedBy: string
  blockedAt: string
  active: boolean
}

export function BlockedUsersTab() {
  const [blockedUsers, setBlockedUsers] = useState<BlockedUser[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  const fetchBlockedUsers = async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/admin/blocked-users')
      setBlockedUsers(data.data ?? [])
    } catch {
      setBlockedUsers([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBlockedUsers()
  }, [])

  const handleUnblock = async (userId: string, userName: string) => {
    if (!window.confirm(`Desbloquear usuário "${userName}"?`)) return
    try {
      await api.post(`/admin/blocked-users/${userId}/unblock`)
      toast.success(`Usuário ${userName} desbloqueado`)
      fetchBlockedUsers()
    } catch {
      toast.error('Erro ao desbloquear usuário')
    }
  }

  const filteredUsers = blockedUsers.filter(
    (u) =>
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()),
  )

  return (
    <div className='space-y-5'>
      {/* Header */}
      <div className='glass-card p-5 flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          <ShieldOff size={20} className='text-red-400' />
          <div>
            <h3 className='text-sm font-semibold text-[var(--font-color-h3)]'>Usuários Bloqueados</h3>
            <p className='text-[12px] text-[var(--font-color-muted)]'>
              Gerencie usuários com acesso suspenso ao sistema
            </p>
          </div>
        </div>
        <div className='flex items-center gap-2'>
          <div className='relative'>
            <Search size={13} className='absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]' />
            <input
              type='text'
              placeholder='Buscar...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className='bg-white/[0.04] border border-white/[0.06] text-text-primary rounded-md pl-8 pr-3 py-2 text-xs outline-none focus:border-accent transition-colors w-48'
            />
          </div>
        </div>
      </div>

      {/* Blocked Users List */}
      <div className='glass-card p-0 overflow-hidden'>
        {loading ? (
          <div className='flex items-center justify-center py-12'>
            <div className='w-5 h-5 border-2 border-[var(--brand-primary)]/30 border-t-[var(--brand-primary)] rounded-full animate-spin' />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className='text-center py-12'>
            <UserX size={32} className='mx-auto text-[var(--font-color-muted)] mb-2 opacity-50' />
            <p className='text-sm text-[var(--font-color-muted)] italic'>
              {search ? 'Nenhum usuário encontrado para esta busca.' : 'Nenhum usuário bloqueado.'}
            </p>
          </div>
        ) : (
          <div className='divide-y divide-[var(--glass-border)]'>
            {filteredUsers.map((user) => (
              <div key={user.id} className='flex items-center justify-between px-4 py-3 hover:bg-[var(--glass-bg)] transition-colors'>
                <div className='flex items-center gap-3'>
                  <div className='w-9 h-9 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center'>
                    <ShieldOff size={16} className='text-red-400' />
                  </div>
                  <div>
                    <p className='text-[13px] font-medium text-[var(--font-color-body)]'>
                      {user.name}
                    </p>
                    <p className='text-[11px] text-[var(--font-color-muted)]'>
                      {user.email}
                    </p>
                    <div className='flex items-center gap-2 mt-0.5'>
                      <Badge variant='danger'>Bloqueado</Badge>
                      <span className='text-[10px] text-[var(--font-color-muted)] flex items-center gap-1'>
                        <Clock size={10} />
                        {formatDate(user.blockedAt)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className='text-right'>
                  <p className='text-[11px] text-[var(--font-color-muted)]'>
                    Motivo: {user.reason}
                  </p>
                  <p className='text-[10px] text-[var(--font-color-muted)]'>
                    Por: {user.blockedBy}
                  </p>
                  <Button
                    variant='ghost'
                    size='sm'
                    icon={<Shield size={12} />}
                    onClick={() => handleUnblock(user.id, user.name)}
                    className='mt-1'
                  >
                    Desbloquear
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
